import {
    BDOT10K_IMPACT_CLASSIFICATION_LABELS,
    BDOT10K_FUNOGBUD_LABELS,
    BDOT10K_LAYER_LABELS,
    FUNKCJA,
    NIER,
    RODZ,
    TYP,
} from '../core/constants';
import { formatPricePerM2 } from '../../shared/number-format';
import { guideLine } from '../../shared/guide-link.js';
import { expandAddressAbbreviations } from './address-abbreviations';

export function fmt(n) {
    if (n == null || n === '') return '—';
    // pl-PL leaves four-digit numbers ungrouped („4481”); group them like the
    // rest of the portal (shared/number-format.js): „4 481”, „1 425,5”.
    return Number(n).toLocaleString('pl-PL').replace(/^(-?\d)(\d{3})(?=$|,)/, '$1\u00a0$2');
}

// A price per square metre of a list card or panel: whole złoty, two decimals
// below 10 zł/m² („5,59”), thousands grouped with a no-break space like fmt().
export function fmtPricePerM2(value) {
    return formatPricePerM2(value).replace(/ /g, '\u00a0');
}

/**
 * Polish plural form: plural(1, 'transakcja', 'transakcje', 'transakcji').
 * 1 → one; 2–4 (except 12–14) → few; everything else, including 0 → many.
 */
export function plural(count, one, few, many) {
    var value = Math.abs(Math.round(Number(count) || 0));
    if (value === 1) return one;
    var lastTwo = value % 100;
    var last = value % 10;
    if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return few;
    return many;
}

/** "155 transakcji", with the number formatted in Polish. */
export function countLabel(count, one, few, many) {
    return fmt(count) + ' ' + plural(count, one, few, many);
}

/** "2025-03-12" → "12 marca 2025"; any other value is returned unchanged. */
export function dateLong(value) {
    if (value == null || value === '') return '';
    var text = String(value);
    var match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (!match) return text;
    var date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (isNaN(date.getTime())) return text;
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function yr(v) {
    return v ? String(v).substring(0, 4) : '—';
}

// The date of the deed of a list or map row in full Polish form ("16 września
// 2026"), or '' without one. The API sends only the normalized date
// (TransakcjeListService::applyTransactionDate(): no placeholder before 1950, no
// raw document text); the version id is the time of publication in the register,
// never the date of the deed.
export function dateDisp(dokDate) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dokDate == null ? '' : dokDate).substring(0, 10));
    return match ? dateLong(match[0]) : '';
}

// What a community signal is about (`target_type`, `coverage_source_type`), in words.
var SIGNAL_TARGET_LABELS = {
    dzialka: 'Działka',
    budynek: 'Budynek',
    miejscowosc: 'Miejscowość',
    ulica: 'Ulica',
    plac: 'Plac',
    adres: 'Punkt adresowy',
    obreb: 'Obręb ewidencyjny',
    jednostka: 'Jednostka ewidencyjna',
    gmina: 'Gmina',
    powiat: 'Powiat',
    wojewodztwo: 'Województwo',
    panstwo: 'Państwo',
    punkt: 'Punkt',
};

/** „dzialka” → „Działka”; a missing or unknown kind is a point on the map. */
export function signalTargetLabel(type) {
    return Object.prototype.hasOwnProperty.call(SIGNAL_TARGET_LABELS, type) ? SIGNAL_TARGET_LABELS[type] : 'Punkt';
}

export function formatDist(m) {
    if (m == null) return '—';
    if (m < 1000) return Math.round(m) + ' m';
    return (m / 1000).toFixed(1).replace('.', ',') + ' km';
}

export function formatAggCount(n) {
    var count = Number(n) || 0;
    if (count <= 999) return String(count);
    if (count <= 9999) return Math.round(count / 100) / 10 + 'k';
    return Math.round(count / 1000) + 'k';
}

export function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function(character) {
        return {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        }[character];
    });
}

/**
 * A cadastral identifier as HTML that may wrap after „.”, „_” and „/”, so a
 * narrow column breaks „…2198_BUD” between its segments and never inside one.
 * Escaped first; for element content only (titles, labels and addresses take
 * the plain text). Twin of CadastralIdentifier::breakableHtml() in PHP.
 */
export function breakableHtml(value) {
    return esc(value).replace(/[._\/]/g, '$&<wbr>');
}

// A link target from data: an http(s) address or a path of this site („/…”,
// never „//host”, which is another site). Browsers drop tabs and line breaks
// inside an address and read „\” as „/”, so „/\host” or „/<TAB>/host” would
// open another site too: a control character or a backslash anywhere rejects
// the address. A space stays; the browser encodes it. Twin of SafeHref::of() in PHP.
var SAFE_HREF_START = /^(?:https?:\/\/|\/(?!\/))/i;
var UNSAFE_HREF_CHARACTER = /[\u0000-\u001f\u007f-\u009f\\]/;

export function safeHref(value) {
    var href = String(value == null ? '' : value).trim();
    return SAFE_HREF_START.test(href) && !UNSAFE_HREF_CHARACTER.test(href) ? href : '';
}

// Street prefixes written in full in addresses ("ul." → "ulica").
export { expandAddressAbbreviations };

// A street with its type (TERYT „cecha”, a field of its own in the building permit register) in
// full, by the rules of EducationFacilityMapService::streetName(): „ul.” → „ulica”, „wyb.” →
// „wybrzeże”, „inne” (other) left out, and no type before a name that already starts with one
// („rynek” and „Rynek” → „Rynek”). The other types are words and stay as they are.
var STREET_TYPES = { 'ul.': 'ulica', 'al.': 'aleja', 'pl.': 'plac', 'os.': 'osiedle', 'bulw.': 'bulwar', 'wyb.': 'wybrzeże', 'inne': '' };
var STREET_TYPE_WORDS = ['ulica', 'aleja', 'aleje', 'plac', 'osiedle', 'bulwar', 'wybrzeże', 'szosa', 'park', 'rondo', 'skwer', 'rynek', 'droga', 'ogród', 'wyspa', 'trakt', 'pasaż', 'most'];

function streetText(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
}

// The type a name opens with, when more of the name follows. The register also writes an
// abbreviated type inside the name („Al. 1000-lecia Państwa Polskiego”, „pl. Grunwaldzki”,
// „Pl. Kościeleckich”; 515 permit rows on the dev database, 26.10.2026): that type is part of the
// name, so it is written in full with a capital, as the register writes a full type word inside a
// name („Aleja Artura Grottgera”): „Aleja”, „Plac”. A full type word stays as written.
function leadingStreetType(name) {
    var space = name.indexOf(' ');
    if (space < 1) return '';
    var lead = name.slice(0, space);
    var key = lead.toLowerCase();
    if (key.slice(-1) === '.') {
        var full = Object.prototype.hasOwnProperty.call(STREET_TYPES, key) ? STREET_TYPES[key] : '';
        return full ? full.charAt(0).toUpperCase() + full.slice(1) : '';
    }
    return STREET_TYPE_WORDS.indexOf(key) !== -1 ? lead : '';
}

// `name` is the main part of the street (`ulica`), `firstName` its first name or title
// (`ulica_dalej`), which the full name puts first: „ul.”, „Maja”, „3” → „ulica 3 Maja”. A name
// that opens with a type takes that type in place of the type field and keeps it before the
// first name, once: „pl.”, „pl. Piłsudskiego”, „marsz. Józefa” → „Plac marsz. Józefa
// Piłsudskiego”; „ul.”, „Al. Krakowska” → „Aleja Krakowska”.
export function streetWithType(type, name, firstName) {
    var main = streetText(name);
    var first = streetText(firstName);
    var lead = leadingStreetType(main);
    if (lead) return [lead, first, main.slice(main.indexOf(' ') + 1)].filter(Boolean).join(' ');
    var street = [first, main].filter(Boolean).join(' ');
    if (!street) return '';
    var key = streetText(type).toLowerCase();
    var word = Object.prototype.hasOwnProperty.call(STREET_TYPES, key) ? STREET_TYPES[key] : key;
    if (!word || STREET_TYPE_WORDS.indexOf(street.split(' ')[0].toLowerCase()) !== -1) return street;
    return word + ' ' + street;
}

export function parseAddr(raw) {
    if (!raw) return '—';
    if (String(raw).indexOf(':') === -1) return expandAddressAbbreviations(raw);

    var segment = String(raw).split('|')[0];
    var fields = {};

    segment.split(';').forEach(function(pair) {
        var parts = pair.split(':');
        if (parts[0] && parts[1]) {
            fields[parts[0].trim()] = parts.slice(1).join(':').trim();
        }
    });

    var output = '';
    if (fields.MSC) output += fields.MSC;
    if (fields.UL) output += (output ? ', ' : '') + 'ulica ' + fields.UL;
    if (fields.NR_PORZ) output += ' ' + fields.NR_PORZ;
    return output ? expandAddressAbbreviations(output) : expandAddressAbbreviations(raw);
}

export function nierLabel(value) {
    return NIER[value] || value || '—';
}

export function rodzLabel(value) {
    return RODZ[value] || value || '—';
}

export function typLabel(value) {
    return TYP[value] || '—';
}

export function funkcjaLabel(v) {
    return FUNKCJA[v] || v || '';
}

export function renderFunkcjaChip(lokFunkcja) {
    if (!lokFunkcja) return '';

    if (typeof lokFunkcja === 'string') {
        return '<span class="nm-chip"><b>Funkcja</b> ' + esc(funkcjaLabel(lokFunkcja)) + '</span>';
    }

    if (Array.isArray(lokFunkcja)) {
        if (lokFunkcja.length === 1) {
            return '<span class="nm-chip"><b>Funkcja</b> ' + esc(funkcjaLabel(lokFunkcja[0])) + '</span>';
        }

        var labels = lokFunkcja.map(funkcjaLabel);
        var tooltip = 'Transakcja obejmuje lokale o różnych funkcjach: ' + labels.join(', ')
            + '. Podana powierzchnia i cena dotyczą całej transakcji.';

        return '<span class="nm-chip nm-chip--info" title="' + esc(tooltip) + '">' +
            '<b>Funkcje</b> ' + esc(labels.join(' + ')) +
            ' <span class="nm-chip-info">ⓘ</span></span>';
    }

    return '';
}

export function bdot10kTranslate(camelStr) {
    if (!camelStr) return '';

    return String(camelStr)
        .split('|')
        .map(function(part) {
            return part.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
        })
        .join(', ');
}

export function bdot10kFunctionItems(props) {
    if (!props) return [];
    var details = props.funszcz_details;

    // MapLibre serializes nested GeoJSON properties to JSON strings while
    // building its internal vector tiles. Normalize them back before rendering
    // so descriptions are not lost after queryRenderedFeatures().
    if (typeof details === 'string' && details.trim() !== '') {
        try {
            details = JSON.parse(details);
        } catch (error) {
            details = [];
        }
    }

    if (Array.isArray(details) && details.length) {
        return details.filter(function(item) {
            return item && typeof item.label_pl === 'string' && item.label_pl.trim() !== '';
        });
    }
    if (props.funszcz_label) {
        return [{ value: props.funszcz || '', label_pl: props.funszcz_label, description_pl: null }];
    }
    return [];
}

export function bdot10kFunctionLabel(props) {
    return bdot10kFunctionItems(props).map(function(item) {
        return item.label_pl;
    }).join(', ');
}

export function escapeHtmlAttribute(value) {
    return esc(value);
}

// The names of the area of a transaction and of its price per square metre by
// type (1 parcels, 2 lokale, 3 buildings); the API sends them as area_label and
// price_per_m2_label, these are the fallbacks (TransakcjeListService::areaPriceLabels).
var TRANSACTION_AREA_LABELS = {
    1: 'Powierzchnia działek',
    2: 'Powierzchnia użytkowa lokalu',
    3: 'Powierzchnia użytkowa budynku'
};
var TRANSACTION_PRICE_PER_M2_LABELS = {
    1: 'Cena za metr kwadratowy działek',
    2: 'Cena za metr kwadratowy powierzchni użytkowej lokalu',
    3: 'Cena za metr kwadratowy powierzchni użytkowej budynku'
};

export function transactionAreaLabel(typ, label) {
    return label || TRANSACTION_AREA_LABELS[Number(typ)] || 'Powierzchnia';
}

export function transactionPricePerM2Label(typ, label) {
    return label || TRANSACTION_PRICE_PER_M2_LABELS[Number(typ)] || 'Cena za metr kwadratowy';
}

// A building sold as a share (API `building_share`: share, usable_area,
// buildings); MapLibre may hand nested feature properties back as JSON text.
export function transactionBuildingShare(value) {
    if (typeof value === 'string' && value.trim() !== '') {
        try {
            value = JSON.parse(value);
        } catch (error) {
            return null;
        }
    }
    return value && typeof value === 'object' && Number(value.buildings) > 0 ? value : null;
}

// A deed of lokale sold as shares (API `lokal_share`: share, usable_area,
// lokale, co_ownership); MapLibre may hand it back as JSON text.
export function transactionLokalShare(value) {
    if (typeof value === 'string' && value.trim() !== '') {
        try {
            value = JSON.parse(value);
        } catch (error) {
            return null;
        }
    }
    return value && typeof value === 'object' && Number(value.lokale) > 0 ? value : null;
}

// Help of a transaction's price per square metre: what is divided by what for
// this type. `example` is the worked division („(500 000 zł ÷ 50 m²)”) or '';
// `share` names the area of the shares of a deed of buildings
// (transactionBuildingShare()) or of lokale (transactionLokalShare()) sold as
// shares; `fromItems` tells that the price per square metre of parcels pairs
// their own prices with their area, so priced parts without that area (an
// access-road share) stay out (TransakcjeListService::pricePerM2Pairing()).
export function transactionPricePerM2Help(typ, label, example, note, share, fromItems) {
    var t = Number(typ);
    var calculation;
    // A share: why its sales stay out of the statistics; otherwise how to read one sale.
    var caution = '';
    var buildingShare = t === 3 ? transactionBuildingShare(share) : null;
    var lokalShare = t === 2 ? transactionLokalShare(share) : null;
    if (lokalShare) {
        var lokalUsable = Number(lokalShare.usable_area) > 0 ? fmt(lokalShare.usable_area) + ' m²' : '';
        calculation = (Number(lokalShare.lokale) > 1
            ? 'cena z aktu podzielona przez powierzchnię odpowiadającą udziałom, czyli powierzchnię użytkową każdego lokalu podaną w akcie pomnożoną przez jego udział'
                + (lokalUsable ? ' (łączna powierzchnia użytkowa lokali: ' + lokalUsable + ')' : '')
            : 'cena z aktu podzielona przez powierzchnię odpowiadającą udziałowi, czyli powierzchnię użytkową lokalu podaną w akcie'
                + (lokalUsable ? ' (' + lokalUsable + ')' : '') + ' pomnożoną przez kupiony udział'
                + (lokalShare.share ? ' ' + lokalShare.share : ''))
            + (example || '') + '.';
        caution = lokalShare.co_ownership
            ? 'udziały w mieszkaniach często zmieniają właściciela między współwłaścicielami lub w rodzinie, dlatego ich sprzedaży nie wliczamy do statystyk mieszkań.'
            : '';
    } else if (buildingShare) {
        share = buildingShare;
        var usable = Number(share.usable_area) > 0 ? fmt(share.usable_area) + ' m²' : '';
        calculation = (Number(share.buildings) > 1
            ? 'cena z aktu podzielona przez powierzchnię odpowiadającą udziałom, czyli powierzchnię użytkową każdego budynku podaną w akcie pomnożoną przez jego udział'
                + (usable ? ' (łączna powierzchnia użytkowa budynków: ' + usable + ')' : '')
            : 'cena z aktu podzielona przez powierzchnię odpowiadającą udziałowi, czyli powierzchnię użytkową budynku podaną w akcie'
                + (usable ? ' (' + usable + ')' : '') + ' pomnożoną przez kupiony udział'
                + (share.share ? ' ' + share.share : ''))
            + (example || '') + '.';
        caution = 'sprzedaży udziałów w budynkach nie wliczamy do statystyk budynków.';
    } else if (t === 3) {
        calculation = 'cena z aktu podzielona przez powierzchnię użytkową budynku podaną w akcie' + (example || '') + '. '
            + 'Gdy akt nie podaje powierzchni użytkowej budynku, ceny za metr kwadratowy budynku nie liczymy; '
            + 'powierzchnia zabudowy (obrys budynku z ewidencji) nie jest powierzchnią użytkową.';
    } else if (t === 1 && fromItems) {
        calculation = 'ceny działek podane w akcie podzielone przez powierzchnię tych działek. '
            + 'Cena udziału w drodze dojazdowej i cena działki bez podanej powierzchni nie wchodzą do tego wyliczenia, '
            + 'więc wynik jest niższy od ceny łącznej podzielonej przez powierzchnię.';
    } else if (t === 1) {
        calculation = 'cena z aktu podzielona przez powierzchnię działek' + (example || '') + '.';
    } else if (t === 2) {
        calculation = 'cena z aktu podzielona przez powierzchnię użytkową lokalu' + (example || '') + '. '
            + 'Gdy akt obejmuje kilka rodzajów lokali, liczymy ją osobno dla każdego rodzaju, '
            + 'jeśli każdy lokal tego rodzaju ma w akcie własną cenę i powierzchnię.';
    } else {
        calculation = 'cena z aktu podzielona przez powierzchnię, której ta cena dotyczy' + (example || '') + '.';
    }
    return transactionPricePerM2Label(typ, label) + '\n'
        + 'Obliczenie: ' + calculation + '\n'
        + (caution
            ? 'Uwaga: ' + caution
            : 'Znaczenie: to cena jednej sprzedaży; poziom cen w okolicy lepiej pokazuje mediana, czyli cena środkowa wielu podobnych transakcji.')
        + (note ? '\n' + note : '')
        + '\nŹródło: Rejestr Cen Nieruchomości — cena brutto z aktu notarialnego.'
        + '\n' + (caution
            ? guideLine('slownik#wspolwlasnosc', 'współwłasność')
            : guideLine('slownik#mediana', 'mediana'));
}

// BDOT10k storeys of a building whose land register has none (`kondygnacje_bdot10k` of
// /api/budynki?pin_only=1 and /api/budynki-hit): the label names the source, the help says
// the value only describes the building (Bdot10kBuildingStoreys on the server).
export var TOPOGRAPHIC_STOREYS_LABEL = 'Kondygnacje nadziemne (Baza Danych Obiektów Topograficznych)';
export var TOPOGRAPHIC_STOREYS_HELP = 'Ewidencja gruntów i budynków nie podaje liczby kondygnacji tego budynku\n'
    + 'Obliczenie: liczbę kondygnacji nadziemnych przepisujemy z budynku Bazy Danych Obiektów Topograficznych, który leży w miejscu budynku z ewidencji.\n'
    + 'Uwaga: z liczby kondygnacji nie liczymy powierzchni użytkowej ani ceny za metr kwadratowy, '
    + 'bo obrys pomnożony przez kondygnacje obejmuje też ściany i części wspólne.\n'
    + 'Źródło: Baza Danych Obiektów Topograficznych Głównego Urzędu Geodezji i Kartografii, warstwa budynków.\n'
    + guideLine('slownik#powierzchnia-uzytkowa', 'powierzchnia użytkowa');
export var TOPOGRAPHIC_STOREYS_HELP_LABEL = 'Skąd pochodzi liczba kondygnacji';

// The BDOT10k storeys to show for a building, or null. Like the building page
// (BudynekSzczegolyRenderer::registerKnowsStoreys()) and the server's fallback, a
// register value of 0 counts as unknown, so the BDOT10k value replaces it.
export function topographicStoreysToShow(props) {
    if (!props || props.kondygnacje_bdot10k == null) return null;
    return Number(props.kondygnacje) > 0 ? null : props.kondygnacje_bdot10k;
}

export function renderInfoHelpButton(popup, ariaLabel) {
    if (!popup) return '';

    return '<button type="button" class="info-popup-trigger" data-info-popup="' + escapeHtmlAttribute(popup) + '"'
        + ' aria-label="' + escapeHtmlAttribute(ariaLabel || 'Wyjaśnienie parametru') + '"'
        + ' title="' + escapeHtmlAttribute(ariaLabel || 'Wyjaśnienie parametru') + '">?</button>';
}

function objectCountLabel(value) {
    var count = Math.abs(Math.round(Number(value) || 0));
    var lastTwo = count % 100;
    var last = count % 10;
    if (count === 1) return 'obiekt';
    if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return 'obiekty';
    return 'obiektów';
}

// Groups of the cadastre („grupa rejestrowa”, OwnershipCategoryHelper) whose public
// owner gave the land in perpetual usufruct („z użytkowaniem wieczystym”).
var PERPETUAL_USUFRUCT_GROUPS = [2, 5, 12, 14];
var OWNERSHIP_GROUP_COUNT = 16;

/**
 * Help popup of the ownership category of a parcel (`grupa_rejestrowa`,
 * `ownership_label` of /api/egib-dzialka), shared by the parcel panel and the
 * parcel card of the tap browse.
 */
export function parcelOwnershipHelp(props) {
    props = props || {};
    var raw = props.grupa_rejestrowa;
    var group = raw == null || raw === '' ? NaN : Number(raw);
    var registerLine = '\nZnaczenie: bez znanej kategorii nie wiadomo, czy właścicielem jest osoba prywatna, gmina, Skarb Państwa czy firma. Właściciela sprawdzisz w księdze wieczystej działki.'
        + '\n' + guideLine('slownik#ksiega-wieczysta', 'księga wieczysta');
    if (!isFinite(group)) {
        return 'Ewidencja gruntów i budynków nie podaje kategorii właściciela tej działki' + registerLine;
    }
    if (group < 1 || group > OWNERSHIP_GROUP_COUNT) {
        return 'Ewidencja gruntów i budynków podaje dla tej działki grupę spoza znanego wykazu' + registerLine;
    }

    var usufruct = PERPETUAL_USUFRUCT_GROUPS.indexOf(group) !== -1;
    return 'Kategoria właściciela działki według ewidencji gruntów i budynków'
        + '\nZnaczenie: mówi, jakiego rodzaju podmiot jest właścicielem lub władającym działką, bez imienia i nazwiska; numer grupy rejestrowej to kod tej kategorii w ewidencji. Ewidencja może nie nadążać za zmianami; właściciela i obciążenia działki potwierdza księga wieczysta.'
        + (usufruct
            ? '\nUwaga: grunt należy do państwa albo samorządu, a użytkownik wieczysty korzysta z niego i płaci opłatę roczną. Sprawdź w księdze wieczystej, czy sprzedający ma własność, czy użytkowanie wieczyste.'
                + '\n' + guideLine('slownik#uzytkowanie-wieczyste', 'użytkowanie wieczyste')
            : '\n' + guideLine('jak-korzystac-z-metroskopu#wlasciciele-sasiednich-dzialek', 'kategoria właściciela działki'));
}

/**
 * Help popup of the median offer price per square metre of a developer investment
 * (DeveloperOffersGeoJsonService: the median over the offers the map shows with the
 * active filters), in the list card and in the investment panel.
 */
export const DEVELOPER_OFFER_MEDIAN_HELP = 'Mediana ceny ofertowej za metr kwadratowy w tej inwestycji'
    + '\nObliczenie: połowa ofert ma niższą cenę za metr kwadratowy, a połowa wyższą. Liczymy ją z ofert tej inwestycji widocznych na mapie przy wybranych filtrach.'
    + '\nZnaczenie: to ceny, które deweloper podaje w cenniku. Ceny zapłacone według aktów notarialnych pokazuje warstwa Transakcje.'
    + '\nŹródło: cenniki deweloperów w portalu dane.gov.pl.'
    + '\n' + guideLine('slownik#mediana', 'mediana');

export function bdot10kImpactHelp(props) {
    props = props || {};
    var baseRadius = Number(props.impact_radius);
    var clusterRadius = Number(props.cluster_impact_radius);
    var multiplier = Number(props.cluster_radius_multiplier);
    var threshold = Number(props.cluster_threshold);
    var sameTypeCount = props.same_type_count == null || props.same_type_count === '' ? NaN : Number(props.same_type_count);
    var maximumRadius = Number(props.maximum_impact_radius);
    var nuisance = Number(props.nuisance_level);
    var usefulness = Number(props.usefulness_level);
    var corridor = props.airport_corridor || null;

    if (!isFinite(clusterRadius) || clusterRadius <= 0) {
        clusterRadius = isFinite(baseRadius) && baseRadius > 0 && isFinite(multiplier) && multiplier > 0
            ? baseRadius * multiplier
            : baseRadius;
    }
    if (!isFinite(maximumRadius) || maximumRadius <= 0) {
        maximumRadius = isFinite(threshold) && threshold > 1 ? clusterRadius : baseRadius;
    }

    var hasRadius = isFinite(baseRadius) && baseRadius > 0;
    var hasCluster = isFinite(threshold) && threshold > 1;
    // The cluster rule of Bdot10kNearbyQueryBuilder: an object beyond its radius still counts
    // when at least `threshold` objects of its kind lie within the extended radius.
    var clusterRule = hasCluster
        ? 'Skupisko to co najmniej ' + threshold + ' ' + objectCountLabel(threshold) + ' tego samego rodzaju w promieniu '
            + formatDist(clusterRadius) + ' — wtedy liczą się wszystkie do tej odległości.'
            + (isFinite(sameTypeCount) ? ' Liczba takich obiektów w tym promieniu: ' + sameTypeCount + '.' : '')
        : '';
    var singleRule = hasRadius ? 'pojedynczy obiekt tego rodzaju liczy się do ' + formatDist(baseRadius) + ' od wybranego miejsca.' : '';
    var clusterCalculation = [singleRule, clusterRule].filter(Boolean).join(' ');

    var source = '\nŹródło: rodzaj obiektu z Bazy Danych Obiektów Topograficznych; oceny wpływu przypisuje Metroskop każdemu rodzajowi obiektu.';
    var corridorSource = '\nŹródło: przebieg pasa startowego z Bazy Danych Obiektów Topograficznych; korytarz wyznacza Metroskop.';
    var corridorGuide = '\n' + guideLine('slownik#obszar-ograniczonego-uzytkowania', 'obszar ograniczonego użytkowania');
    var extendKm = (corridor && corridor.extend_km) || '15';

    var nuisanceHelp = 'Jak bardzo taki obiekt zwykle przeszkadza sąsiadom'
        + '\nSkala: od 0 (brak typowej uciążliwości) do 5 (bardzo duża uciążliwość).'
        + '\nUwaga: ocena dotyczy rodzaju obiektu i nie jest pomiarem hałasu ani zanieczyszczeń. Poziom hałasu przy dużych drogach, kolei i lotniskach pokaże mapa hałasu.'
        + source
        + '\n' + guideLine('jak-korzystac-z-metroskopu#cisza-i-woda', 'mapa hałasu');
    if (corridor && props.impact_trigger === 'corridor') {
        // AirportCorridorService::resolveNuisanceLevel(): linear decrease from the runway, rounded up, at least 1.
        var maximumCorridorNuisance = Number(corridor.maximum_nuisance_level);
        var distance = Number(props.dist_m);
        nuisanceHelp = 'Orientacyjna uciążliwość hałasu samolotów'
            + '\nObliczenie: '
            + (isFinite(maximumCorridorNuisance) ? 'przy pasie startowym ocena wynosi ' + maximumCorridorNuisance + ' na 5' : 'ocena jest najwyższa przy pasie startowym')
            + ' i maleje równomiernie z odległością aż do końca korytarza, ' + extendKm + ' kilometrów od pasa; w korytarzu wynosi co najmniej 1.'
            + (props.dist_m != null && isFinite(distance) ? ' To miejsce leży ' + formatDist(distance) + ' od pasa.' : '')
            + '\nUwaga: to model odległości, bez pomiaru hałasu i rzeczywistych tras lotów. Sprawdź, czy lotnisko ma obszar ograniczonego użytkowania, i posłuchaj na miejscu o różnych porach dnia.'
            + corridorSource
            + corridorGuide;
    }

    return {
        classification: 'Ogólna ocena wpływu takiego obiektu na sąsiedztwo'
            + '\nZnaczenie: „Uciążliwy” zwykle przeszkadza mieszkańcom, na przykład hałasem albo zapachem; „Użyteczny” ułatwia codzienne życie; „Neutralny” nie ma wyraźnego wpływu. Ocena dotyczy całego rodzaju obiektów, więc konkretny obiekt obejrzyj na miejscu.'
            + source,
        nuisance: nuisanceHelp,
        usefulness: 'Jak bardzo taki obiekt zwykle ułatwia codzienne życie'
            + '\nSkala: od 0 (brak typowej korzyści) do 5 (bardzo duża korzyść).'
            + '\nUwaga: ocena dotyczy rodzaju obiektu; godziny otwarcia i dostępność konkretnego miejsca trzeba sprawdzić osobno.'
            + source,
        directTrigger: 'Wybrane miejsce leży w zasięgu wpływu tego obiektu'
            + (singleRule ? '\nObliczenie: ' + singleRule + ' Ten obiekt leży bliżej.' : '')
            + source,
        clusterTrigger: 'Obiekt liczy się jako część skupiska podobnych obiektów'
            + (clusterCalculation ? '\nObliczenie: ' + clusterCalculation : '')
            + '\nZnaczenie: wiele podobnych obiektów razem wpływa na okolicę mocniej niż jeden, dlatego liczy się też ten dalszy obiekt.'
            + source,
        corridorTrigger: 'Wybrane miejsce leży w korytarzu podejścia do lotniska'
            + '\nObliczenie: korytarz przedłuża oś pasa startowego o ' + extendKm + ' kilometrów w obie strony i zwęża się z odległością od lotniska; orientacyjnie tędy przebiegają starty i podejścia do lądowania.'
            + '\nUwaga: to orientacyjny model, bez rzeczywistych tras lotów i pomiarów hałasu. Sprawdź, czy lotnisko ma obszar ograniczonego użytkowania.'
            + corridorSource
            + corridorGuide,
        radius: 'Odległość, do której pojedynczy taki obiekt zwykle wpływa na otoczenie'
            + '\nZnaczenie: ' + (hasRadius ? 'obiekt tego rodzaju bliżej niż ' + formatDist(baseRadius) + ' od wybranego miejsca liczy się do otoczenia zawsze.' : 'obiekt tego rodzaju w tym zasięgu liczy się do otoczenia zawsze.')
            + (hasCluster ? ' ' + clusterRule : '')
            + source,
        cluster: 'Obiekty tego rodzaju w pobliżu i próg skupiska'
            + '\nObliczenie: pierwsza liczba to obiekty tego samego rodzaju'
            + (isFinite(clusterRadius) && clusterRadius > 0 ? ' w promieniu ' + formatDist(clusterRadius) : ' w pobliżu')
            + ', druga — ile ich trzeba, żeby liczyło się całe skupisko.'
            + '\nZnaczenie: po osiągnięciu progu liczą się też obiekty dalsze niż'
            + (hasRadius ? ' ' + formatDist(baseRadius) : ' zasięg pojedynczego obiektu')
            + ', bo wiele podobnych obiektów razem wpływa na okolicę mocniej niż jeden.'
            + source,
        balance: 'Która ocena przeważa: użyteczność czy uciążliwość'
            + '\nObliczenie: '
            + (isFinite(nuisance) && isFinite(usefulness) ? 'użyteczność ' + usefulness + ' na 5 minus uciążliwość ' + nuisance + ' na 5. ' : 'użyteczność minus uciążliwość, obie w skali od 0 do 5. ')
            + 'Różnica co najmniej 2 w którąkolwiek stronę oznacza przewagę, 0 — wpływ zrównoważony, 1 — wpływ mieszany.'
            + source,
        maximumRadius: 'Najdalsza odległość, z której obiekty tej kategorii wpływają na wybrane miejsce'
            + '\nObliczenie: ' + (hasCluster
                ? clusterCalculation
                : 'to zasięg pojedynczego obiektu tego rodzaju' + (hasRadius ? ', ' + formatDist(maximumRadius) : '') + '; tej kategorii nie liczymy w skupiskach.')
            + '\nZnaczenie: dalsze obiekty tego rodzaju nie trafiają na listę otoczenia.'
            + source,
    };
}

export function renderBdot10kFunctions(props, escapeHtml) {
    var items = bdot10kFunctionItems(props);
    if (!items.length) return '';

    return '<span class="nm-funszcz-list">' + items.map(function(item) {
        var label = item.label_pl;
        var description = typeof item.description_pl === 'string' ? item.description_pl.trim() : '';
        var help = '';
        if (description) {
            var popupHtml = '<div class="info-popup-title">' + escapeHtml(label) + '</div>'
                + '<p><strong>Opis:</strong> ' + escapeHtml(description) + '</p>'
                + '<p><strong>Źródło:</strong> opis funkcji obiektu w Bazie Danych Obiektów Topograficznych.</p>';
            help = '<button type="button" class="info-popup-trigger" data-info-popup="' + escapeHtmlAttribute(popupHtml) + '"'
                + ' aria-label="Opis funkcji: ' + escapeHtmlAttribute(label) + '" title="Opis funkcji: ' + escapeHtmlAttribute(label) + '">?</button>';
        }
        return '<span class="nm-funszcz-item">' + escapeHtml(label) + help + '</span>';
    }).join('') + '</span>';
}

export function bdot10kLabel(props) {
    if (!props) return 'Obiekt';
    if (props.label) return props.label;
    if (props.nazwa) return props.nazwa;
    if (props.object_label) return props.object_label;
    if (props.funszcz_label) return props.funszcz_label;
    if (props.funszcz_details) return bdot10kFunctionLabel(props);
    if (props.funszcz) return bdot10kTranslate(props.funszcz);
    if (props.rodzaj_label) return props.rodzaj_label;
    if (props.rodzaj) return bdot10kTranslate(props.rodzaj);
    if (props.funogbud) return BDOT10K_FUNOGBUD_LABELS[props.funogbud] || bdot10kTranslate(props.funogbud);
    return bdot10kLayerLabel(props) || 'Obiekt';
}

/**
 * The BDOT10k layer of an object in words („Budynki”, „Oświata”); empty when it
 * has no readable name, so a raw code such as „OT_BUBD_A” is never shown.
 */
export function bdot10kLayerLabel(props) {
    if (!props) return '';
    if (props.layer_label) return String(props.layer_label);
    return Object.prototype.hasOwnProperty.call(BDOT10K_LAYER_LABELS, props.layer_code)
        ? BDOT10K_LAYER_LABELS[props.layer_code]
        : '';
}

export function bdot10kImpactClassificationLabel(props) {
    if (!props) return '';
    if (props.impact_classification_label) return props.impact_classification_label;
    return BDOT10K_IMPACT_CLASSIFICATION_LABELS[props.impact_classification] || '';
}

export function bdot10kSpatialImpactSummary(props) {
    if (!props) return '';

    var impactRadius = Number(props.impact_radius);
    if (!isFinite(impactRadius) || impactRadius <= 0) {
        return '';
    }

    var threshold = Number(props.cluster_threshold);
    var sameTypeCount = Number(props.same_type_count);
    var clusterImpactRadius = Number(props.cluster_impact_radius);
    var parts = [];

    if (props.impact_trigger_label) {
        parts.push(String(props.impact_trigger_label));
    } else if (props.impact_trigger === 'cluster') {
        parts.push('wpływ skupiska');
    } else if (props.impact_trigger === 'direct') {
        parts.push('wpływ bezpośredni');
    }

    parts.push('promień ' + formatDist(impactRadius));

    if (isFinite(threshold) && threshold > 1) {
        var clusterRadius = isFinite(clusterImpactRadius) && clusterImpactRadius > 0 ? clusterImpactRadius : impactRadius;
        if (isFinite(sameTypeCount) && sameTypeCount > 0) {
            parts.push('skupisko ' + sameTypeCount + '/' + threshold + ' w ' + formatDist(clusterRadius));
        } else {
            parts.push('próg skupiska ' + threshold + ' w ' + formatDist(clusterRadius));
        }
    }

    return parts.join(' · ');
}

export function bdot10kImpactSummary(props) {
    if (!props) return '';

    var parts = [];
    var classificationLabel = bdot10kImpactClassificationLabel(props);
    var spatialSummary = bdot10kSpatialImpactSummary(props);

    if (classificationLabel) {
        parts.push(classificationLabel);
    }
    if (props.nuisance_level != null && props.nuisance_level !== '') {
        parts.push('uciążliwość ' + props.nuisance_level + '/5');
    }
    if (props.usefulness_level != null && props.usefulness_level !== '') {
        parts.push('użyteczność ' + props.usefulness_level + '/5');
    }
    if (spatialSummary) {
        parts.push(spatialSummary);
    }

    return parts.join(' · ');
}

export function bdot10kImpactBalanceLabel(props) {
    if (!props) return '';

    var nuisanceLevel = Number(props.nuisance_level);
    var usefulnessLevel = Number(props.usefulness_level);

    if (!isFinite(nuisanceLevel) || !isFinite(usefulnessLevel)) {
        return '';
    }

    var difference = usefulnessLevel - nuisanceLevel;

    if (difference >= 2) {
        return 'przewaga użyteczności';
    }
    if (difference <= -2) {
        return 'przewaga uciążliwości';
    }
    if (difference === 0) {
        return 'wpływ zrównoważony';
    }

    return 'wpływ mieszany';
}

/*
 * A building permit's description of works names private investors, so the API sends only its safe
 * excerpt (`opis`, GunbWorks::excerpt()): the register's own words up to the first word that could
 * begin a name, an address or a parcel number. Panels quote it as the register's words, and where
 * there is room they say where it ends, as the guided check does.
 */
export const GUNB_EXCERPT_LABEL = 'Opis w rejestrze';
export const GUNB_EXCERPT_NOTE = 'Opis robót z rejestru pokazujemy do pierwszego słowa, które może być nazwiskiem, adresem albo numerem działki.';

/** The excerpt in quotation marks („Budowa obory na 46 DJP…”); '' without one. */
export function gunbExcerptQuote(excerpt) {
    return excerpt ? '„' + excerpt + '”' : '';
}

/*
 * The detail panel alone also gets the whole description with every word that may identify a private
 * person replaced by a marker (`opisPelny`, GunbMaskedDescription::offer()), when it says more than the
 * excerpt, and whether it has markers (`opisPelnyZnaczniki`). The „?” after the excerpt opens it in the
 * shared popup (info-popup.js): titled GUNB_MASKED_TITLE, the text quoted as the register's words right
 * under the title (`data-popup-quote`) and, only when there are markers, the note and the meaning of each
 * marker at its end (GunbMaskedDescriptionHelp::title(), help() and markers(), the same words as the
 * permit and parcel pages). The panel shows one permit, so its one trigger carries the text itself.
 */
export const GUNB_MASKED_TITLE = 'Pełny opis z rejestru (bez danych osób)';
export const GUNB_MASKED_NOTE = 'Imiona i nazwiska osób prywatnych, numery ich domów, numery mieszkań, telefonów, PESEL i inne dane, które mogą wskazać osobę prywatną, zastąpiliśmy znacznikami w nawiasach kwadratowych.';
export const GUNB_MASKED_MARKERS = 'Znaczenie: [osoba] zastępuje imię i nazwisko osoby prywatnej, [adres] adres e-mail, stronę internetową albo adres, w którym może być imię lub nazwisko, [numer] numer domu osoby prywatnej, numer mieszkania, telefonu, PESEL, dowodu osobistego, paszportu albo księgi wieczystej, a […] słowo, które może być imieniem albo nazwiskiem.';

/** The „?” that opens the masked description `masked`; `markers` adds the note on them to the popup. */
export function gunbMaskedDescriptionButton(masked, markers) {
    var help = markers ? GUNB_MASKED_TITLE + '\n' + GUNB_MASKED_NOTE + '\n' + GUNB_MASKED_MARKERS : GUNB_MASKED_TITLE;
    return '<button type="button" class="info-popup-trigger" data-info-popup="' + escapeHtmlAttribute(help) + '"'
        + ' aria-label="' + escapeHtmlAttribute(GUNB_MASKED_TITLE) + '"'
        + ' data-popup-quote="' + escapeHtmlAttribute(gunbExcerptQuote(masked)) + '">?</button>';
}
